import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import {
  DateAdapter,
  ErrorStateMatcher,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  ShowOnDirtyErrorStateMatcher,
} from '@angular/material/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  PreloadAllModules,
  Router,
  provideRouter,
  withDisabledInitialNavigation,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideTransloco } from '@ngneat/transloco';
import { mockApiServices } from 'app/mock-api';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { MSALGuardConfigFactory, MSALInstanceFactory } from '../main';
import { BrowserUtils } from '@azure/msal-browser';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { environment } from '../environments/environment';
import * as Sentry from '@sentry/angular';
import { SentryFeedbackService } from './core/services/sentry-feedback.service';
import { CachingInterceptor } from './shared/interceptors/caching.interceptor';
// import {
//   MAT_COLOR_FORMATS,
//   NGX_MAT_COLOR_FORMATS,
//   NgxMatColorPickerModule,
// } from '@angular-material-components/color-picker';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { Scheme } from '../@fuse/services/config';
import { ApiConfiguration } from './api/api-configuration';
import { provideFormly } from './shared/components/formly-fields/formly-root-config';
import { RemoveDataInterceptor } from './shared/interceptors/remove-data.interceptor';
import { authInterceptor } from './core/auth/auth.interceptor';
import { impersonationInterceptor } from './core/auth/impersonation.interceptor';
import { isDevMode } from '@angular/core';

registerLocaleData(localeNl);

const initialNavigation =
  !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
    ? withEnabledBlockingInitialNavigation()
    : withDisabledInitialNavigation();

const dutchRangeLabel = (page: number, pageSize: number, length: number) => {
  if (length == 0 || pageSize == 0) {
    return `0 van ${length}`;
  }

  length = Math.max(length, 0);

  const startIndex = page * pageSize;

  // If the start index exceeds the list length, do not try and fix the end index to the end.
  const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;

  return `${startIndex + 1} - ${endIndex} van ${length}`;
};

export function getDutchPaginatorIntl() {
  const paginatorIntl = new MatPaginatorIntl();

  paginatorIntl.itemsPerPageLabel = 'Items per pagina:';
  paginatorIntl.nextPageLabel = 'Volgende pagina';
  paginatorIntl.previousPageLabel = 'Vorige pagina';
  paginatorIntl.getRangeLabel = dutchRangeLabel;

  return paginatorIntl;
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Zoneless change detection — only fires CD on signal writes, event
    // listeners, async pipes, markForCheck/detectChanges, and AfterRender
    // callbacks. Avoids the per-microtask zone.js fan-out that previously
    // re-evaluated every signal-based template binding in the planning view.
    provideZonelessChangeDetection(),

    // Sentry's feedback widget no longer injects its own floating button (#20919), so the app owns
    // when and where a trigger exists — which means the service has to be alive on every surface.
    // initialDataResolver, which used to be the only thing constructing it, runs on authenticated
    // routes only; the 'empty' shell (sign-in, sign-out, /home, 404) has no resolver at all and
    // would silently lose feedback. Constructing it at bootstrap is what makes the coverage rule
    // in SentryFeedbackService true everywhere instead of almost everywhere.
    provideEnvironmentInitializer(() => {
      inject(SentryFeedbackService);
    }),

    // { provide: MAT_COLOR_FORMATS, useValue: NGX_MAT_COLOR_FORMATS },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 10000 } },

    // CDK ≥21 renders overlays as native top-layer popovers by default. With a
    // connected overlay (mat-menu) the popover host is positioned at the panel and
    // the transparent backdrop only receives clicks within that region — clicks in
    // the page gutters neither hit the backdrop nor close the menu (#20842 QA).
    // Classic .cdk-overlay-container rendering keeps the backdrop full-viewport.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },

    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
    // Same rule as main.ts (#20997): the config decides, not the build mode. `isDevMode()` is true
    // in every non-optimized build (`test`, `demo`), which used to strip the error handler there.
    ...(environment.sentry.enabled
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
          },
        ]
      : []),

    importProvidersFrom(MsalRedirectComponent),
    provideHttpClient(
      withInterceptors([CachingInterceptor, RemoveDataInterceptor, authInterceptor, impersonationInterceptor])
    ),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    // importProvidersFrom(NgxMatColorPickerModule),
    provideFormly(),
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    provideAnimationsAsync(),
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      initialNavigation
    ),

    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: ['dd/MM/yyyy', 'dd-MM-yyyy', 'yyyy-MM-dd', 'DDD'],
        },
        display: {
          dateInput: 'dd-MM-yyyy', // Default display format
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'dd-MM-yyyy',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },
    { provide: MAT_DATE_LOCALE, useValue: 'nl-NL' },
    { provide: MatPaginatorIntl, useValue: getDutchPaginatorIntl() },

    // TODO api config provider url
    {
      provide: ApiConfiguration,
      useValue: { rootUrl: environment.apiConfig.uri },
    },

    // Transloco Config
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: 'nl',
            label: 'Dutch',
          },
        ],
        defaultLang: 'nl',
        fallbackLang: 'nl',
        reRenderOnLangChange: true,
        prodMode: true,
      },
      loader: TranslocoHttpLoader,
    }),
    // {
    //   // Preload the default language before the app starts to prevent empty/jumping content
    //   provide: APP_INITIALIZER,
    //   useFactory: () => {
    //     const translocoService = inject(TranslocoService);
    //     const defaultLang = translocoService.getDefaultLang();
    //     translocoService.setActiveLang(defaultLang);
    //
    //     return () => firstValueFrom(translocoService.load(defaultLang));
    //   },
    //   multi: true,
    // },

    // Fuse
    provideAuth(),
    provideIcons(),
    provideFuse({
      mockApi: {
        delay: 0,
        services: mockApiServices,
      },
      fuse: {
        layout: localStorage.getItem('theme-settings')
          ? JSON.parse(localStorage.getItem('theme-settings'))['layout']
          : 'classy',
        scheme: localStorage.getItem('theme') ? (localStorage.getItem('theme') as Scheme) : 'auto',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-default',
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-brand',
            name: 'Brand',
          },
        ],
      },
    }),

    // Service Worker - enable PWA functionality
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // Dev-only: a service worker registered by an earlier production-like build keeps serving its
    // cached bundle even though the app no longer registers one in dev (enabled: !isDevMode()).
    // That silently masks code changes on localhost. Proactively unregister any lingering worker
    // and drop its caches so `ng serve` always reflects the latest build.
    provideEnvironmentInitializer(() => {
      if (isDevMode() && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        navigator.serviceWorker
          .getRegistrations()
          .then(registrations => registrations.forEach(registration => registration.unregister()))
          .catch(() => undefined);
        if ('caches' in window) {
          caches
            .keys()
            .then(keys => keys.forEach(key => caches.delete(key)))
            .catch(() => undefined);
        }
      }
    }),
  ],
};
